/**
 * ShowCancelledToggle — reveals or re-hides cancelled workshops on a calendar
 * or list (BON-624). Styled after the calendar's subregion legend pills; the
 * dot is the red the venue table's Cancelled pill uses.
 *
 * With a `count` of zero there is nothing to reveal, so it renders nothing. A
 * server-filtered list does not know its count and omits it.
 */
import type { ReactElement } from 'react';

import { CANCELLED_COLOR } from './cancelled-workshops';

export interface ShowCancelledToggleProps {
  showing: boolean;
  onToggle: () => void;
  count?: number;
}

function toggleLabel(showing: boolean, count: number | undefined): string {
  if (showing) return 'Hide cancelled';
  return count === undefined ? 'Show cancelled' : `Show ${count} cancelled`;
}

export function ShowCancelledToggle({
  showing,
  onToggle,
  count,
}: ShowCancelledToggleProps): ReactElement | null {
  if (count === 0 && !showing) return null;
  return (
    <button
      type="button"
      aria-pressed={showing}
      onClick={onToggle}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        border: '1px solid var(--nb-border)',
        background: showing ? 'var(--nb-surface)' : 'transparent',
        fontSize: 10.5,
        fontWeight: 600,
        color: 'var(--nb-ink-2)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <i
        aria-hidden="true"
        style={{ width: 6, height: 6, borderRadius: 999, background: CANCELLED_COLOR }}
      />
      {toggleLabel(showing, count)}
    </button>
  );
}
